import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ViewWillEnter } from '@ionic/angular';
import { of, Observable, combineLatest, firstValueFrom } from 'rxjs';
import {
  catchError,
  mergeMap,
  map,
  delay,
  tap,
  startWith,
} from 'rxjs/operators';
import { PATHS } from '@shared/constants/paths';
import { ToastService } from '@shared/services/toast/toast.service';
import { MessageTypeEnum } from '@app/ngrx-message/constants/messageTypeEnum';
import { TranslateService } from '@ngx-translate/core';
import { UserService } from '@app/user/services';
import { UsfTokenService } from '@panamax/app-state';

interface TokenUserData {
  email: string;
  userName: string;
  userId: string;
  guid: string;
  addDtm: Date;
}

@Component({
  selector: 'app-email-unsubscribe',
  templateUrl: './email-unsubscribe.page.html',
  styleUrls: ['./email-unsubscribe.page.scss'],
  standalone: false,
})
export class EmailUnsubscribePage implements OnInit, ViewWillEnter {
  private static readonly CATEGORIES = [
    'PAYMENT',
    'ORDER',
    'DELIVERY',
    'DIRECT',
    'TM_NOTE',
    'SERVICE_REQUEST',
  ];
  public selectedCategories: Set<string> = new Set();
  public isAllSelected = false;

  // Individual boolean properties for template performance
  public isPaymentSelected = false;
  public isOrderSelected = false;
  public isDeliverySelected = false;
  public isDirectSelected = false;
  public isTmNoteSelected = false;
  public isServiceRequestSelected = false;
  public tokenUserData$: Observable<TokenUserData | null> = of(null);
  public userVm$: Observable<{
    isPremierUser: boolean;
    isLoggedIn: boolean;
    loaded: boolean;
  }> = of({
    isPremierUser: false,
    isLoggedIn: false,
    loaded: false,
  });

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
    private readonly toastService: ToastService,
    private readonly translateService: TranslateService,
    private readonly userService: UserService,
    private readonly tokenService: UsfTokenService,
  ) {}

  ngOnInit(): void {
    this.userVm$ = combineLatest([
      this.userService
        .userProfiles$()
        .pipe(
          map(userProfiles =>
            (userProfiles ?? []).some(
              profile =>
                profile.key.toLowerCase() === 'axerossouser' &&
                profile.value.toLowerCase() === 'y',
            ),
          ),
        ),
      this.userService.isGuestUser$(),
    ]).pipe(
      map(([isPremierUser, isGuestUser]) => ({
        isPremierUser: isPremierUser,
        isLoggedIn: !isGuestUser,
        loaded: true,
      })),
      startWith({
        isPremierUser: false,
        isLoggedIn: false,
        loaded: false,
      }),
    );
  }

  ionViewWillEnter() {
    this.tokenUserData$ = this.activatedRoute.queryParams.pipe(
      map(params => [params['token'], params['userId']]),
      mergeMap(([token, userId]) => {
        if (!token || !userId) {
          throw new Error('invalid token or userId');
        }
        return this.getEmailTokenData();
      }),
      catchError(() => {
        return of(null);
      }),
    );
  }

  private getEmailTokenData(): Observable<TokenUserData> {
    // WIP STUB
    // TODO: Replace stub with actual HTTP call:
    const mockResponse = {
      email: 'user@usfoods.com',
      userName: 'John Doe',
      userId: '1234567890',
      guid: '1234567890',
      addDtm: new Date(),
    };

    return of(mockResponse);
  }

  private updateSelectionFlags(): void {
    this.isPaymentSelected = this.selectedCategories.has('PAYMENT');
    this.isOrderSelected = this.selectedCategories.has('ORDER');
    this.isDeliverySelected = this.selectedCategories.has('DELIVERY');
    this.isDirectSelected = this.selectedCategories.has('DIRECT');
    this.isTmNoteSelected = this.selectedCategories.has('TM_NOTE');
    this.isServiceRequestSelected =
      this.selectedCategories.has('SERVICE_REQUEST');
  }

  private toggleAll(isChecked: boolean) {
    this.isAllSelected = isChecked;
    this.selectedCategories = isChecked
      ? new Set(EmailUnsubscribePage.CATEGORIES)
      : new Set();
    this.updateSelectionFlags();
  }

  routeToManageAlerts() {
    this.router.navigate([PATHS.NOTIFICATIONS, PATHS.NOTIFICATION_PREFERENCES]);
  }

  routeToLogin() {
    this.tokenService.navigateToLogin();
  }

  onUnsubscribeAllChange(event: CustomEvent<{ checked: boolean }>) {
    const isChecked = event.detail.checked;
    this.toggleAll(isChecked);
  }

  onUnsubscribeChange(category: string, event?: any) {
    const isChecked: boolean = event.detail.checked;

    if (isChecked) {
      this.selectedCategories.add(category);
      if (
        this.selectedCategories.size === EmailUnsubscribePage.CATEGORIES.length
      ) {
        this.isAllSelected = true;
      }
    } else {
      this.selectedCategories.delete(category);
      this.isAllSelected = false;
    }

    this.updateSelectionFlags();
  }

  public onCancel() {
    this.toggleAll(false);
  }

  public async onSubmit() {
    try {
      await firstValueFrom(
        of({
          success: true, // replace with API call
        }).pipe(
          delay(3000),
          tap(response => {
            if (response.success) {
              this.toastService.presentToastMsg(
                this.translateService.instant(
                  'i18n.notifications.emailUnsubscribe.unsubscribeSuccess',
                ),
                'green-toast',
                MessageTypeEnum.success,
                [],
              );
            } else {
              throw new Error('unsubscribe failed');
            }
          }),
        ),
      );
    } catch (error) {
      this.toastService.presentToastMsg(
        this.translateService.instant(
          'i18n.notifications.emailUnsubscribe.unsubscribeFail',
        ),
        'red-toast',
        MessageTypeEnum.error,
        [],
      );
    }
  }

  get hasSelectedCategories(): boolean {
    return this.selectedCategories.size > 0;
  }
}
